import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Activity,
  BarChart3,
  CalendarRange,
  Download,
  Filter,
  Mail,
  MessageSquare,
  Printer,
  RefreshCw,
  Save,
} from "lucide-react";
import { ThemeContext } from "../../../context/ThemeContext";
import api from "../../../api/axios";
import { normalizeTrackingListResponse } from "../../../utils/trackingSummariesApi";
import "./view-usages-rep-modal.css";
import "../settings/myadmin-list.css";

function stripHtml(html) {
  if (!html || typeof html !== "string") return "";
  const d = document.createElement("div");
  d.innerHTML = html;
  return (d.textContent || d.innerText || "").trim();
}

function formatDateTime(dateString) {
  if (!dateString) return "—";
  try {
    return new Date(dateString).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(dateString);
  }
}

function formatDateShort(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(iso);
  }
}

function formatCurrency(amount) {
  if (amount == null || amount === "") return "—";
  const n = Number(String(amount).replace(/,/g, ""));
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(n);
}

function getActionCount(actions) {
  if (!actions || typeof actions !== "object") return 0;
  return Object.values(actions).reduce((sum, count) => sum + (Number(count) || 0), 0);
}

function typeLabel(type) {
  if (type === "seller2Net") return "Seller 2 Net";
  if (type === "propnine") return "Prop 19";
  if (!type) return "—";
  return String(type);
}

function typeClass(type) {
  if (type === "buyer") return "vur__type vur__type--buyer";
  if (type === "seller") return "vur__type vur__type--seller";
  if (type === "seller2Net") return "vur__type vur__type--seller2net";
  if (type === "refinance") return "vur__type vur__type--refinance";
  if (type === "propnine") return "vur__type vur__type--propnine";
  return "vur__type vur__type--default";
}

const GROUP_LABELS = {
  daily: "Day",
  type: "Calculator type",
  device: "Device",
  user: "User",
  company: "Company",
};

function groupBySectionTitle(groupBy) {
  return GROUP_LABELS[groupBy] || groupBy || "Period";
}

const ACTIVITY_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Same windowed page list as Settings → User management (`MyAdmin.js`). */
function getPaginationRange(totalPagesArg, currentPage) {
  if (totalPagesArg <= 3) return [...Array(totalPagesArg)].map((_, i) => i + 1);
  if (currentPage === 1) return [1, 2, 3];
  if (currentPage === totalPagesArg) return [totalPagesArg - 2, totalPagesArg - 1, totalPagesArg];
  return [currentPage - 1, currentPage, currentPage + 1];
}

/**
 * Usage stats for a single user (company rep / MyAdmin).
 * GET /admin/tracking-summaries — paginated activity rows (`data.trackingSummaries`, `data.pagination.total`, optional `data.summary`).
 * GET /admin/tracking-summaries/statistics — aggregates + optional grouped breakdown (groupBy).
 */
export default function ViewUsagesRepModel({ user }) {
  const theme = useContext(ThemeContext);
  const maincompanyid = theme?.id ?? "";

  const userId = user?.id;
  const displayName = useMemo(() => {
    const fn = (user?.first_name || "").trim();
    const ln = (user?.last_name || "").trim();
    if (fn || ln) return [fn, ln].filter(Boolean).join(" ");
    return (user?.name || "").trim() || "User";
  }, [user]);
  const emailPlain = useMemo(() => stripHtml(user?.email), [user]);

  const avatarLetter = useMemo(() => {
    const c = (displayName || "?").trim().charAt(0);
    return c ? c.toUpperCase() : "?";
  }, [displayName]);

  const [type, setType] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [groupBy, setGroupBy] = useState("daily");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortBy] = useState("created_at");
  const [sortOrder] = useState("desc");

  const [trackingSummaries, setTrackingSummaries] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);
  const [error, setError] = useState(null);

  const buildListParams = useCallback(() => {
    const params = {
      page,
      limit: pageSize,
      sort: sortBy,
      order: sortOrder,
      maincompanyid: maincompanyid || undefined,
      userId: userId ?? undefined,
    };
    if (type) params.type = type;
    if (createdFrom) params.createdFrom = createdFrom;
    if (createdTo) params.createdTo = createdTo;
    return Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== "" && v !== undefined && v !== null)
    );
  }, [page, pageSize, sortBy, sortOrder, maincompanyid, userId, type, createdFrom, createdTo]);

  const fetchTrackingSummaries = useCallback(async () => {
    if (!userId || !maincompanyid) {
      setTrackingSummaries([]);
      setTotal(0);
      setLoading(false);
      setError(!maincompanyid ? "Company context is not available." : "User is not specified.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/tracking-summaries", { params: buildListParams() });
      const normalized = normalizeTrackingListResponse(data);
      setTrackingSummaries(normalized.trackingSummaries);
      setTotal(normalized.total);
    } catch (e) {
      console.error("ViewUsagesRepModel: tracking list failed", e);
      setTrackingSummaries([]);
      setTotal(0);
      setError(
        e?.response?.data?.message ||
        "Could not load usage activity. You may not have access to reports for this user."
      );
    } finally {
      setLoading(false);
    }
  }, [userId, maincompanyid, buildListParams]);

  const fetchStatistics = useCallback(async () => {
    if (!userId || !maincompanyid) {
      setStatistics(null);
      setStatsLoading(false);
      setStatsError(null);
      return;
    }
    setStatsLoading(true);
    setStatsError(null);
    try {
      const params = {
        dateFrom: createdFrom || undefined,
        dateTo: createdTo || undefined,
        groupBy,
        maincompanyid,
        userId,
      };
      if (type) params.type = type;
      const clean = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== "" && v !== undefined)
      );
      const { data } = await api.get("/admin/tracking-summaries/statistics", { params: clean });
      if (data?.data) {
        setStatistics({
          ...data.data.totalNetSheetActions,
          ...data.data.actionDistribution,
          groupedData: data.data.usageStats,
          period: data.data.period,
        });
      } else {
        setStatistics(null);
      }
    } catch (e) {
      console.error("ViewUsagesRepModel: statistics failed", e);
      setStatistics(null);
      setStatsError(
        e?.response?.data?.message || "Summary numbers could not be loaded. The activity list below may still be available."
      );
    } finally {
      setStatsLoading(false);
    }
  }, [userId, maincompanyid, createdFrom, createdTo, groupBy, type]);

  useEffect(() => {
    fetchTrackingSummaries();
  }, [fetchTrackingSummaries]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  /** If total shrinks (e.g. filters), avoid staying on an empty page. */
  useEffect(() => {
    if (loading || total <= 0) return;
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [total, pageSize, loading, page]);

  const handleApply = useCallback(() => {
    setPage(1);
    fetchTrackingSummaries();
    fetchStatistics();
  }, [fetchTrackingSummaries, fetchStatistics]);

  const handleClearFilters = () => {
    setType("");
    setCreatedFrom("");
    setCreatedTo("");
    setGroupBy("daily");
    setPage(1);
    setPageSize(10);
  };

  const handleActivityPageSizeChange = (e) => {
    const next = Number(e.target.value);
    if (!ACTIVITY_PAGE_SIZE_OPTIONS.includes(next)) return;
    setPageSize(next);
    setPage(1);
  };

  const printedTotal =
    statistics != null
      ? (Number(statistics.totalPrintedDetailed) || 0) + (Number(statistics.totalPrintedQuick) || 0)
      : null;

  const filterRangeLabel = useMemo(() => {
    if (createdFrom && createdTo) return `${formatDateShort(createdFrom)} – ${formatDateShort(createdTo)}`;
    if (createdFrom) return `From ${formatDateShort(createdFrom)}`;
    if (createdTo) return `Until ${formatDateShort(createdTo)}`;
    return "All dates";
  }, [createdFrom, createdTo]);

  const apiPeriodLabel = useMemo(() => {
    const p = statistics?.period;
    if (!p) return null;
    const from = p.from ?? p.start ?? p.dateFrom;
    const to = p.to ?? p.end ?? p.dateTo;
    if (from && to) return `${formatDateShort(from)} – ${formatDateShort(to)}`;
    if (from) return formatDateShort(from);
    return null;
  }, [statistics]);

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = total === 0 ? 0 : Math.min(page * pageSize, total);

  if (!userId) {
    return (
      <div className="vur">
        <div className="vur__error" role="alert">
          <p className="vur__error-title">No user selected</p>
          Close this window and open usage again from the user list.
        </div>
      </div>
    );
  }

  return (
    <div className="vur">
      <header className="vur__profile">
        <div className="vur__avatar" aria-hidden="true">
          {avatarLetter}
        </div>
        <div className="vur__profile-text">
          <h2 className="vur__intro-name">{displayName}</h2>
          <p className="vur__intro-meta">{emailPlain || "—"}</p>
          <p className="vur__intro-hint">
            Totals and the breakdown table follow your filters and <strong>group by</strong>. The activity list is paginated
            separately with the same calculator and date filters.
          </p>
        </div>
      </header>

      <div className="vur__filters-wrap">
        <div className="vur__filters-head">
          <Filter size={14} aria-hidden />
          Filters
        </div>
        <div className="vur__filters" role="search" aria-label="Usage filters">
          <div className="vur__field">
            <label htmlFor="vur-type">Calculator</label>
            <select
              id="vur-type"
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All types</option>
              <option value="buyer">Buyer</option>
              <option value="seller">Seller</option>
              <option value="refinance">Refinance</option>
              <option value="seller2Net">Seller 2 Net</option>
              <option value="propnine">Prop 19</option>
            </select>
          </div>
          <div className="vur__field">
            <label htmlFor="vur-from">
              From <span className="vur__field-hint">(optional)</span>
            </label>
            <input
              id="vur-from"
              type="date"
              value={createdFrom}
              onChange={(e) => {
                setCreatedFrom(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="vur__field">
            <label htmlFor="vur-to">
              To <span className="vur__field-hint">(optional)</span>
            </label>
            <input
              id="vur-to"
              type="date"
              value={createdTo}
              onChange={(e) => {
                setCreatedTo(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="vur__field">
            <label htmlFor="vur-group">Group breakdown by</label>
            <select id="vur-group" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              <option value="daily">Day</option>
              <option value="type">Calculator type</option>
              <option value="device">Device</option>
            </select>
            <span className="vur__field-hint">Affects summary &amp; grouped rows only</span>
          </div>
          <div className="vur__actions">
            <button
              type="button"
              className="vur__btn vur__btn--primary"
              onClick={handleApply}
              disabled={loading}
              title="Reload list and summary with current filters"
            >
              <RefreshCw size={15} strokeWidth={2.25} />
              Refresh
            </button>
            <button type="button" className="vur__btn vur__btn--ghost" onClick={handleClearFilters}>
              Reset
            </button>
          </div>
        </div>
      </div>

      <section className="vur__section" aria-labelledby="vur-summary-heading">
        <div className="vur__section-head">
          <div>
            <h3 id="vur-summary-heading" className="vur__section-title">
              Summary
            </h3>
            <p className="vur__section-sub">
              <span className="vur__inline-icon" aria-hidden>
                <CalendarRange size={12} strokeWidth={2.25} />
              </span>
              Range: {filterRangeLabel}
              {apiPeriodLabel ? ` · Report window: ${apiPeriodLabel}` : ""}
            </p>
          </div>
        </div>

        {statsLoading && (
          <div className="vur__skeleton-grid" aria-busy="true" aria-label="Loading summary">
            {[1, 2, 3, 4, 5, 6].map((k) => (
              <div key={k} className="vur__skeleton-card" />
            ))}
          </div>
        )}

        {statsError && !statsLoading && (
          <div className="vur__note" role="status">
            {statsError}
          </div>
        )}

        {!statsLoading && statistics && (
          <div className="vur__stats" aria-label="Summary counts">
            <div
              className="vur__stat vur__stat--actions"
              style={{
                "--vur-stat-accent": "#2563eb",
                "--vur-stat-icon-bg": "rgba(37, 99, 235, 0.12)",
              }}
            >
              <div className="vur__stat-top">
                <div>
                  <p className="vur__stat-label">Total actions</p>
                  <p className="vur__stat-value">{statistics.totalNetSheetActions ?? 0}</p>
                </div>
                <div className="vur__stat-icon">
                  <Activity strokeWidth={2.25} />
                </div>
              </div>
            </div>
            <div
              className="vur__stat"
              style={{
                "--vur-stat-accent": "#059669",
                "--vur-stat-icon-bg": "rgba(5, 150, 105, 0.12)",
              }}
            >
              <div className="vur__stat-top">
                <div>
                  <p className="vur__stat-label">Emailed</p>
                  <p className="vur__stat-value">{statistics.totalEmailed ?? 0}</p>
                  {statistics.emailed != null && (
                    <p className="vur__stat-hint">{statistics.emailed}% of actions</p>
                  )}
                </div>
                <div className="vur__stat-icon">
                  <Mail strokeWidth={2.25} />
                </div>
              </div>
            </div>
            <div
              className="vur__stat"
              style={{
                "--vur-stat-accent": "#7c3aed",
                "--vur-stat-icon-bg": "rgba(124, 58, 237, 0.12)",
              }}
            >
              <div className="vur__stat-top">
                <div>
                  <p className="vur__stat-label">Printed</p>
                  <p className="vur__stat-value">{printedTotal ?? 0}</p>
                  {statistics.printed != null && (
                    <p className="vur__stat-hint">{statistics.printed}% of actions</p>
                  )}
                </div>
                <div className="vur__stat-icon">
                  <Printer strokeWidth={2.25} />
                </div>
              </div>
            </div>
            <div
              className="vur__stat"
              style={{
                "--vur-stat-accent": "#ea580c",
                "--vur-stat-icon-bg": "rgba(234, 88, 12, 0.12)",
              }}
            >
              <div className="vur__stat-top">
                <div>
                  <p className="vur__stat-label">Downloaded</p>
                  <p className="vur__stat-value">{statistics.totalDownloaded ?? 0}</p>
                  {statistics.downloaded != null && (
                    <p className="vur__stat-hint">{statistics.downloaded}% of actions</p>
                  )}
                </div>
                <div className="vur__stat-icon">
                  <Download strokeWidth={2.25} />
                </div>
              </div>
            </div>
            <div
              className="vur__stat"
              style={{
                "--vur-stat-accent": "#0891b2",
                "--vur-stat-icon-bg": "rgba(8, 145, 178, 0.12)",
              }}
            >
              <div className="vur__stat-top">
                <div>
                  <p className="vur__stat-label">Texted</p>
                  <p className="vur__stat-value">{statistics.totalTexted ?? 0}</p>
                </div>
                <div className="vur__stat-icon">
                  <MessageSquare strokeWidth={2.25} />
                </div>
              </div>
            </div>
            <div
              className="vur__stat"
              style={{
                "--vur-stat-accent": "#475569",
                "--vur-stat-icon-bg": "rgba(71, 85, 105, 0.12)",
              }}
            >
              <div className="vur__stat-top">
                <div>
                  <p className="vur__stat-label">Saved</p>
                  <p className="vur__stat-value">{statistics.totalSaved ?? 0}</p>
                </div>
                <div className="vur__stat-icon">
                  <Save strokeWidth={2.25} />
                </div>
              </div>
            </div>
          </div>
        )}

        {!statsLoading && !statistics && !statsError && (
          <p className="vur__empty" style={{ padding: "20px 12px" }}>
            No summary data for these filters.
          </p>
        )}

        {statistics?.groupedData?.length > 0 && (
          <>
            <h4 className="vur__section-title vur__section-title--row" style={{ marginTop: 14, marginBottom: 8 }}>
              <BarChart3 size={14} strokeWidth={2.25} aria-hidden />
              By {groupBySectionTitle(groupBy)}
            </h4>
            <div className="vur__table-wrap">
              <table className="vur__table">
                <thead>
                  <tr>
                    <th scope="col">Period / group</th>
                    <th scope="col" className="vur__table-num">
                      Actions
                    </th>
                    <th scope="col" className="vur__table-num">
                      Emailed
                    </th>
                    <th scope="col" className="vur__table-num">
                      Printed
                    </th>
                    <th scope="col" className="vur__table-num">
                      Downloaded
                    </th>
                    <th scope="col" className="vur__table-num">
                      Texted
                    </th>
                    <th scope="col" className="vur__table-num">
                      Saved
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {statistics.groupedData.map((row, idx) => (
                    <tr key={row.period ?? row.userid ?? idx}>
                      <td>{row.period != null ? String(row.period) : "—"}</td>
                      <td className="vur__table-num">{row.totalNetSheetActions ?? 0}</td>
                      <td className="vur__table-num">{row.totalEmailed ?? 0}</td>
                      <td className="vur__table-num">
                        {(Number(row.totalPrintedDetailed) || 0) + (Number(row.totalPrintedQuick) || 0)}
                      </td>
                      <td className="vur__table-num">{row.totalDownloaded ?? 0}</td>
                      <td className="vur__table-num">{row.totalTexted ?? 0}</td>
                      <td className="vur__table-num">{row.totalSaved ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <section className="vur__section vur__section--activity" aria-labelledby="vur-activity-heading">
        <div className="vur__section-head">
          <div>
            <h3 id="vur-activity-heading" className="vur__section-title">
              Activity
            </h3>
            <p className="vur__section-sub">Each row is one net sheet record for this user.</p>
          </div>
        </div>

        {error && (
          <div className="vur__error" role="alert">
            <p className="vur__error-title">Could not load activity</p>
            {error}
          </div>
        )}

        <div className="vur__table-wrap vur__table-wrap--activity">
          {loading ? (
            <div className="vur__skeleton-table" aria-busy="true" aria-label="Loading activity">
              {Array.from({ length: Math.min(pageSize, 12) }, (_, k) => (
                <div key={k} className="vur__skeleton-row" />
              ))}
            </div>
          ) : !error && trackingSummaries.length === 0 ? (
            <div className="vur__empty">
              <p className="vur__empty-title">No activity in this range</p>
              Try widening the date range or clearing the calculator filter.
            </div>
          ) : !error ? (
            <table className="vur__table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Prepared for</th>
                  <th scope="col">Property</th>
                  <th scope="col" className="vur__table-num">
                    Value
                  </th>
                  <th scope="col">Type</th>
                  <th scope="col" className="vur__table-num">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {trackingSummaries.map((item) => (
                  <tr key={item.id != null ? String(item.id) : `${item.created_at}-${item.mrsid}`}>
                    <td>{formatDateTime(item.created_at)}</td>
                    <td>{item.preparedfor || "—"}</td>
                    <td>{item.address || "—"}</td>
                    <td className="vur__table-num">{formatCurrency(item.salesprice)}</td>
                    <td>
                      <span className={typeClass(item.type)}>{typeLabel(item.type)}</span>
                    </td>
                    <td className="vur__table-num">{getActionCount(item.actions)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>

        {!loading && !error && total > 0 && (
          <div className="myadmin-toolbar">
            <span className="myadmin-toolbar__range">
              {`Showing ${showingFrom}–${showingTo} of ${total}`}
            </span>

            <div className="myadmin-toolbar__pagesize">
              <label htmlFor="vur-activity-page-size">Rows per page</label>
              <select
                id="vur-activity-page-size"
                value={pageSize}
                onChange={handleActivityPageSizeChange}
              >
                {ACTIVITY_PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            {totalPages > 1 && (
              <nav className="myadmin-pager" aria-label="Activity list pagination">
                <button
                  type="button"
                  className="myadmin-pager__btn"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Prev
                </button>
                {getPaginationRange(totalPages, page).map((p) => (
                  <button
                    type="button"
                    key={p}
                    className={`myadmin-pager__btn ${page === p ? "myadmin-pager__btn--active" : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  className="myadmin-pager__btn"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </nav>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
